import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import barba from '@barba/core';
import { startArticleTransition } from './article-transition';

gsap.registerPlugin(ScrollTrigger);

// ─────────────────────────────────────────────
// Réglages
// ─────────────────────────────────────────────

const P1 = 1;    // section 1 · phase 1 : la photo du hero se rétracte jusqu'à la carte 01
const P2 = 2.6;  // section 1 · phase 2 : défilement horizontal du carrousel

const PARALLAX_SCALE = 1.15;
const CAROUSEL_SCROLL = 0.65; // distance de scroll du carrousel / distance parcourue (1 = 1px pour 1px ; plus petit = plus rapide)
const PARALLAX_X     = 6; // xPercent, de -6 à +6 pendant le défilement

// Section 2 — longueurs de scroll, en hauteurs d'écran
const SPLIT_VH      = 0.9;  // transition : dernière carte → deux moitiés → titres
const SPLIT_HOLD_VH = 0.5;  // pause épinglée pour jouer avec le partage
const SPLIT_READY   = 0.6;  // fraction de la transition à partir de laquelle le survol s'active (Design en place)
const COVER_VH      = 1;    // le split reste fixe pendant 1 écran : la section suivante monte par-dessus
const SPLIT_MIN = 0.3;  // limites du déséquilibre au survol
const SPLIT_MAX = 0.7;
const SPLIT_DIM = 0.7;  // assombrissement max du côté non survolé (0 = aucun, 1 = noir)

// Contour extérieur du logo (rect + polygone), coordonnées du viewBox 22.7353 × 23.5258
const LOGO_VB  = { w: 22.7353, h: 23.5258 };
const LOGO_PTS = [[0.5, 8.7003], [8.5077, 8.7003], [8.5077, 0.6925], [22.2353, 5.2423], [22.2353, 23.0258], [0.5, 23.0258]];

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

const getEls = (container) => ({
    stage:     container.querySelector('#home-stage'),
    media:     container.querySelector('#home-hero-media'),
    img:       container.querySelector('#home-hero-img'),
    dark:      container.querySelector('#home-hero-dark'),
    content:   container.querySelector('#home-hero-content'),
    title:     container.querySelector('#home-hero-title'),
    label:     container.querySelector('#home-hero-label'),
    divider:   container.querySelector('#home-hero-divider'),
    intention: container.querySelector('#home-hero-intention'),
    worksHead: container.querySelector('#home-works-head'),
    track:     container.querySelector('#home-track'),
    progress:  container.querySelector('#home-progress'),
    fill:      container.querySelector('#home-fill'),
    counter:   container.querySelector('#home-counter'),
    halfA:     container.querySelector('#home-pa'),
    halfD:     container.querySelector('#home-pd'),
});

const getSiteChrome = () => [document.querySelector('body > header'), document.getElementById('menu-btn')].filter(Boolean);

const isMobile = () => window.innerWidth < 768;

// Découpe le H1 en lettres (cibles du vol depuis le loader). Les lignes restent en nowrap : pas de césure.
export const splitHomeTitle = (title) => {
    if (!title) return [];
    if (!title.dataset.split) {
        title.querySelectorAll('.line').forEach((line) => {
            const txt = line.textContent.trim();
            line.textContent = '';
            [...txt].forEach((c) => {
                const s = document.createElement('span');
                s.className = 'ch';
                s.style.display = 'inline-block';
                s.textContent = c;
                line.appendChild(s);
            });
        });
        title.dataset.split = 'true';
    }
    return [...title.querySelectorAll('.ch')];
};

// Boîte du glyphe (et non du span à largeur fixe du loader)
const glyphRect = (el) => {
    const r = document.createRange();
    r.selectNodeContents(el);
    return r.getBoundingClientRect();
};

// Coordonnées relatives à l'élément découpé (et non à la fenêtre) :
// la barre d'admin WordPress ou tout autre décalage ne fausse plus la forme
const logoPolygon = (logo, target) => {
    const b  = logo.getBoundingClientRect();
    const m  = target.getBoundingClientRect();
    const k  = Math.min(b.width / LOGO_VB.w, b.height / LOGO_VB.h);
    const ox = b.left - m.left + (b.width - LOGO_VB.w * k) / 2;
    const oy = b.top - m.top + (b.height - LOGO_VB.h * k) / 2;
    return 'polygon(' + LOGO_PTS.map(([x, y]) => `${(ox + x * k).toFixed(1)}px ${(oy + y * k).toFixed(1)}px`).join(', ') + ')';
};

const fullPolygon = (target) => {
    const W = target.offsetWidth, H = target.offsetHeight, x1 = W * 0.37;
    return `polygon(0px 0px, ${x1}px 0px, ${x1}px 0px, ${W}px 0px, ${W}px ${H}px, 0px ${H}px)`;
};

// Chaque lettre du loader vole jusqu'à sa lettre dans le H1
const buildFlight = (sources, targets) => {
    const tl = gsap.timeline();
    sources.forEach((s, i) => {
        const g = glyphRect(s);
        const t = glyphRect(targets[i]);
        const b = s.getBoundingClientRect();
        gsap.set(s, { transformOrigin: `${g.left - b.left}px ${g.top - b.top}px` });
        tl.to(s, {
            x: t.left - g.left,
            y: t.top - g.top,
            scale: t.height / g.height,
            duration: 1.45,
            ease: 'expo.inOut',
        }, i * 0.018);
    });
    return tl;
};

// ─────────────────────────────────────────────
// Intro (premier chargement de la home uniquement)
// Appelée par initLoader une fois le logo dessiné et le nom révélé.
// La photo apparaît dans la forme du logo, qui s'ouvre jusqu'au plein écran,
// pendant que les lettres du loader rejoignent le H1.
// ─────────────────────────────────────────────

export const playHomeReveal = async ({ bg, logo, subtitle, chars }) => {
    const container = document.querySelector('[data-barba-namespace="front"]');
    if (!container) return;

    const els     = getEls(container);
    const chrome  = getSiteChrome();
    const targets = splitHomeTitle(els.title);
    const sources = chars.filter((s) => s.dataset.ch !== ' ');

    // La photo doit être décodée avant d'apparaître dans le logo
    if (els.img) {
        try { await els.img.decode(); } catch (e) { /* image cassée : on continue */ }
    }

    // État de départ : la scène passe entre le fond (20000) et le contenu (20002) du loader
    els.stage.style.zIndex = '20001';
    els.stage.style.backgroundColor = 'transparent'; // laisse voir le fond noir du loader pendant l'intro
    gsap.set(els.track, { visibility: 'hidden' });
    gsap.set([els.worksHead, els.progress], { autoAlpha: 0 });
    gsap.set(targets, { visibility: 'hidden' });
    gsap.set([els.label, els.intention], { yPercent: 120 });
    gsap.set(els.divider, { scaleX: 0, transformOrigin: 'left center' });
    gsap.set(chrome, { autoAlpha: 0 });
    gsap.set(els.media, { autoAlpha: 0, clipPath: logoPolygon(logo, els.media) });

    return new Promise((resolve) => {
        const r = gsap.timeline({
            onComplete: () => {
                gsap.set(targets, { visibility: 'visible' });
                gsap.set(sources, { autoAlpha: 0 });
                gsap.set(els.media, { clearProps: 'clipPath' });
                gsap.set(els.img, { clearProps: 'filter' });
                els.stage.style.zIndex = '';
                els.stage.style.backgroundColor = '';
                resolve();
            },
        });

        // Vol des lettres (si le H1 ne correspond pas au loader : simple fondu)
        if (sources.length === targets.length) {
            r.add(buildFlight(sources, targets), 0.35);
        } else {
            r.to(sources, { autoAlpha: 0, duration: 0.4 }, 0.35);
            r.set(targets, { visibility: 'visible' }, 1.2);
        }

        r.to(subtitle, { opacity: 0, duration: 0.3 }, 0);

        // Photo dans le logo → plein écran
        r.to(els.media, { autoAlpha: 1, duration: 0.45, ease: 'power2.out' }, 0);
        // La photo naît assombrie dans le logo et s'éclaire pendant l'ouverture :
        // pas de passage brutal du noir du loader à une image claire
        r.fromTo(els.img,
            { scale: 1.4, filter: 'brightness(0.3)' },
            { scale: 1, filter: 'brightness(1)', duration: 1.9, ease: 'expo.inOut' }, 0.25);
        r.to(logo, { opacity: 0, duration: 0.4 }, 0.35);
        r.to(els.media, { clipPath: fullPolygon(els.media), duration: 1.4, ease: 'expo.inOut' }, 0.4);
        // Le fond noir du loader s'efface en fondu (il est déjà recouvert par la photo à ce moment)
        r.to(bg, { autoAlpha: 0, duration: 0.6, ease: 'power1.out' }, 1.5);

        // Le reste du hero arrive derrière le nom, ligne par ligne
        r.to(els.label,     { yPercent: 0, duration: 0.8, ease: 'power3.out' }, 1.45);
        r.to(els.divider,   { scaleX: 1, duration: 0.7, ease: 'power3.out' }, 1.6);
        r.to(els.intention, { yPercent: 0, duration: 0.9, ease: 'power3.out' }, 1.7);
        r.to(chrome,        { autoAlpha: 1, duration: 0.8 }, 1.65);
    });
};

// ─────────────────────────────────────────────
// Section 2 — géométrie des deux moitiés
// s = part de l'Architecture (0.5 = égalité).
// Desktop : Architecture à gauche, Design à droite. Mobile : en haut / en bas.
// ─────────────────────────────────────────────

const halfGeom = (stage) => {
    const W = () => stage.offsetWidth;
    const H = () => stage.offsetHeight;
    return {
        archi: (s) => isMobile()
            ? { top: 0, left: 0, width: W(), height: H() * s }
            : { top: 0, left: 0, width: W() * s, height: H() },
        design: (s) => isMobile()
            ? { top: H() * s, left: 0, width: W(), height: H() * (1 - s) }
            : { top: 0, left: W() * s, width: W() * (1 - s), height: H() },
        // Architecture s'ouvre depuis la jonction vers l'extérieur
        archiClosed: () => (isMobile() ? 'inset(100% 0% 0% 0%)' : 'inset(0% 0% 0% 100%)'),
    };
};

// ─────────────────────────────────────────────
// Scroll : hero → carte 01 → carrousel → Architecture / Design → contact
// Appelée à chaque entrée sur la home (premier chargement ou transition Barba).
// ─────────────────────────────────────────────

export const initHomeFlow = (container) => {
    const els = getEls(container);
    if (!els.stage || !els.track || !els.media) return;

    const cards = [...els.track.querySelectorAll('.lw-slide')];
    if (!cards.length) return;

    ScrollTrigger.getById('home-flow')?.kill(true);

    // Hero en état final (fin d'intro, ou arrivée par Barba sans intro)
    const targets = splitHomeTitle(els.title);
    gsap.set(targets, { visibility: 'visible' });
    gsap.set(els.media, { autoAlpha: 1, clearProps: 'clipPath' });
    gsap.set([els.label, els.intention], { yPercent: 0 });
    gsap.set(els.divider, { scaleX: 1, transformOrigin: 'left center' });
    gsap.set(els.track, { visibility: 'visible' });
    gsap.set(getSiteChrome(), { clearProps: 'opacity,visibility' });

    // Clic carte → transition article existante
    cards.forEach((card) => {
        if (card.dataset.flowInit) return;
        card.dataset.flowInit = 'true';
        card.addEventListener('click', () => startArticleTransition(card));
    });

    const first      = cards[0];
    const last       = cards[cards.length - 1];
    const firstImg   = first.querySelector('.lw-img');
    const firstMeta  = first.querySelector('.lw-meta');
    const rest       = [...els.track.children].slice(1);
    const notLast    = cards.filter((c) => c !== last);
    const cardImgs   = cards.map((c) => c.querySelector('.lw-img img')).filter(Boolean);
    const total      = cards.length;

    const maxX = () => Math.max(0, els.track.scrollWidth + els.track.offsetLeft * 2 - window.innerWidth);

    gsap.set(firstImg, { autoAlpha: 0 });
    gsap.set(firstMeta, { autoAlpha: 0 });
    gsap.set(cardImgs, { scale: PARALLAX_SCALE, xPercent: -PARALLAX_X });

    // ── Section 2 : préparation ──
    const hasSplit = !!(els.halfA && els.halfD) && cards.length > 1;
    const geom     = halfGeom(els.stage);
    const split    = { s: 0.5 };
    let interactive = false;

    const halfTexts = hasSplit ? container.querySelectorAll('.home-half-txt .mask > *') : [];

    const placeArchi = () => gsap.set(els.halfA, geom.archi(0.5));

    if (hasSplit) {
        placeArchi();
        gsap.set(els.halfA, { clipPath: geom.archiClosed(), autoAlpha: 1, visibility: 'hidden' });
        gsap.set(els.halfD, { autoAlpha: 0 });
        gsap.set(halfTexts, { yPercent: 110 });
    }

    // Rectangle de la dernière carte quand le carrousel est en bout de course
    const lastRect = () => ({
        top:    els.track.offsetTop + last.offsetTop,
        left:   els.track.offsetLeft + last.offsetLeft - maxX(),
        width:  last.offsetWidth,
        height: last.offsetHeight,
    });

    // Déséquilibre au survol : la moitié vers laquelle on va s'élargit
    const renderSplit = () => {
        const s = split.s;
        gsap.set(els.halfA, geom.archi(s));
        gsap.set(els.halfD, geom.design(s));
        if (isMobile()) return;

        const k    = gsap.utils.mapRange(SPLIT_MIN, SPLIT_MAX, -1, 1, s); // +1 → archi domine
        const grow = (v) => (v >= 0 ? 1 + v * 0.15 : 1 + v * 0.38);      // dominant +15 %, tassé −38 %
        gsap.set(els.halfA.querySelector('.home-half-txt'), { scale: grow(k) });
        gsap.set(els.halfD.querySelector('.home-half-txt'), { scale: grow(-k) });
        gsap.set(els.halfA.querySelector('.home-half-dim'), { opacity: Math.max(0, -k) * SPLIT_DIM });
        gsap.set(els.halfD.querySelector('.home-half-dim'), { opacity: Math.max(0, k) * SPLIT_DIM });
        gsap.set([els.halfA.querySelector('img'), els.halfD.querySelector('img')], { x: (s - 0.5) * 60 });
    };

    const toSplit = gsap.quickTo(split, 's', { duration: 0.9, ease: 'power3.out', onUpdate: renderSplit });

    // Dernière position connue de la souris (fraction de la largeur), null si hors de la scène
    let pointerX = null;
    const splitFromPointer = (x) => gsap.utils.mapRange(0, 1, SPLIT_MAX, SPLIT_MIN, x); // la moitié survolée grandit

    const setInteractive = (on) => {
        if (!hasSplit || on === interactive) return;
        interactive = on;
        if (on && pointerX !== null && !isMobile()) toSplit(splitFromPointer(pointerX));
        if (!on) {
            gsap.killTweensOf(split);
            split.s = 0.5;
            renderSplit();
        }
    };

    const updateProgress = (tl) => {
        const p = gsap.utils.clamp(0, 1, (tl.time() - P1) / P2);
        if (els.fill) els.fill.style.width = (p * 100) + '%';
        if (els.counter) {
            const idx = Math.min(total, Math.round(p * (total - 1)) + 1);
            els.counter.textContent = String(idx).padStart(2, '0') + ' / ' + String(total).padStart(2, '0');
        }
    };

    // La section qui suit la scène remonte d'un écran : elle arrive par le bas pendant le dernier
    // écran du pin et recouvre le split resté fixe. Posé en JS : sans JS, la page reste en flux normal.
    const coverNext = hasSplit ? container.querySelector('#home-practice') : null;
    if (coverNext) {
        coverNext.style.marginTop = `-${COVER_VH * 100}vh`;
        coverNext.style.position = 'relative';
        coverNext.style.zIndex = '10';
    }

    // ScrollTrigger répartit le scroll au prorata des durées de la timeline :
    // on convertit les longueurs voulues (en écrans) dans l'unité de la section 1
    const s1Px       = window.innerHeight * 1.1 + maxX() * CAROUSEL_SCROLL;
    const toUnits    = (vh) => (P1 + P2) * (window.innerHeight * vh) / s1Px;
    const splitUnits = toUnits(SPLIT_VH);
    const holdUnits  = toUnits(SPLIT_HOLD_VH);
    const coverUnits = toUnits(COVER_VH);

    const tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
            id: 'home-flow',
            trigger: els.stage,
            start: 'top top',
            end: () => '+=' + (window.innerHeight * 1.1 + maxX() * CAROUSEL_SCROLL + (hasSplit ? window.innerHeight * (SPLIT_VH + SPLIT_HOLD_VH + COVER_VH) : 0)),
            pin: true,
            scrub: 1,
            invalidateOnRefresh: true,
            onRefresh: () => {
                if (!hasSplit) return;
                placeArchi();
                if (interactive) renderSplit();
            },
        },
        onUpdate: () => {
            updateProgress(tl);
            // Le survol s'active dès que les deux moitiés sont ouvertes, pas en fin de pin
            if (hasSplit) setInteractive(tl.time() >= P1 + P2 + splitUnits * SPLIT_READY);
        },
    });

    // ── Section 1 · Phase 1 — la photo du hero se rétracte et prend la place de la carte 01 ──
    tl.to(els.content, { y: -70, opacity: 0, duration: 0.35, ease: 'power1.in' }, 0)
      .fromTo(els.media,
          { top: 0, left: 0, width: () => els.stage.offsetWidth, height: () => els.stage.offsetHeight },
          {
              top:    () => els.track.offsetTop + first.offsetTop,
              left:   () => els.track.offsetLeft + first.offsetLeft,
              width:  () => first.offsetWidth,
              height: () => first.offsetHeight,
              duration: P1,
              ease: 'power2.inOut',
          }, 0)
      .fromTo(els.img,
          { scale: 1, xPercent: 0 },
          { scale: PARALLAX_SCALE, xPercent: -PARALLAX_X, duration: P1, ease: 'power2.inOut' }, 0)
      .to(els.dark, { opacity: 0, duration: P1 * 0.8 }, 0)
      .fromTo(rest,
          { x: () => window.innerWidth * 0.55, opacity: 0 },
          { x: 0, opacity: 1, duration: 0.65, stagger: 0.05, ease: 'power2.out' }, 0.32)
      .fromTo(els.worksHead, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3 }, 0.6)
      .fromTo(els.progress,  { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3 }, 0.7)
      .to(firstMeta, { autoAlpha: 1, duration: 0.2 }, 0.85)
      // Passage de relais invisible : la vraie carte 01 remplace la photo du hero
      .set(firstImg, { autoAlpha: 1 }, P1)
      .set(els.media, { autoAlpha: 0 }, P1);

    // ── Section 1 · Phase 2 — défilement horizontal + parallaxe interne des images ──
    // x arrondi au pixel : évite les positions entre deux pixels (liserés sur les bords des cartes)
    tl.to(els.track, {
        x: () => -maxX(),
        duration: P2,
        modifiers: { x: (x) => Math.round(parseFloat(x)) + 'px' },
    }, P1)
      .fromTo(cardImgs, { xPercent: -PARALLAX_X }, { xPercent: PARALLAX_X, duration: P2 }, P1);

    // ── Section 2 — la dernière carte devient la moitié Design, Architecture s'ouvre à côté ──
    // Construite à part (durées « naturelles »), puis compressée à SPLIT_VH écrans de scroll
    if (hasSplit) {
        const T  = P1 + P2;
        const s2 = gsap.timeline({ defaults: { ease: 'none' } });
        const designImg = els.halfD.querySelector('img');
        const archiImg  = els.halfA.querySelector('img');

        // Le carrousel se retire, sauf la dernière carte
        s2.to(notLast, { x: -60, opacity: 0, duration: 0.45, stagger: 0.03, ease: 'power2.in' }, 0)
          .to([els.worksHead, els.progress], { autoAlpha: 0, duration: 0.3 }, 0);

        // Relais : la moitié Design se pose sur la dernière carte, puis s'étend
        s2.set(els.halfD, { top: () => lastRect().top, left: () => lastRect().left, width: () => lastRect().width, height: () => lastRect().height }, 0.15)
          .to(els.halfD, { autoAlpha: 1, duration: 0.12 }, 0.15)
          .set(last, { autoAlpha: 0 }, 0.27)
          .to(els.halfD, {
              top:    () => geom.design(0.5).top,
              left:   () => geom.design(0.5).left,
              width:  () => geom.design(0.5).width,
              height: () => geom.design(0.5).height,
              duration: 0.8,
              ease: 'power3.inOut',
          }, 0.2);
        // L'image part du cadrage exact de la carte (taille de la carte + parallaxe en fin de course),
        // puis s'élargit jusqu'au format de la moitié : aucun saut de cadrage au relais
        if (designImg) {
            s2.fromTo(designImg,
                isMobile()
                    ? { width: () => lastRect().width, height: () => lastRect().height, scale: PARALLAX_SCALE, xPercent: PARALLAX_X }
                    : { width: () => lastRect().width, right: 0, scale: PARALLAX_SCALE, xPercent: PARALLAX_X },
                isMobile()
                    ? { width: () => els.stage.offsetWidth, height: () => els.stage.offsetHeight, scale: 1, xPercent: 0, duration: 0.8, ease: 'power3.inOut' }
                    : { width: () => els.stage.offsetWidth * 0.74 + 80, right: -40, scale: 1, xPercent: 0, duration: 0.8, ease: 'power3.inOut' },
                0.2);
        }

        // Architecture s'ouvre depuis la jonction
        s2.set(els.halfA, { visibility: 'visible' }, 0.75)
          .to(els.halfA, { clipPath: 'inset(0% 0% 0% 0%)', duration: 0.7, ease: 'power3.inOut' }, 0.75);
        if (archiImg) s2.fromTo(archiImg, { scale: 1.12 }, { scale: 1, duration: 0.9, ease: 'power2.out' }, 0.75);

        // Titres
        s2.to(halfTexts, { yPercent: 0, duration: 0.5, stagger: 0.05, ease: 'power3.out' }, 1.15);

        // Compression : la transition dure SPLIT_VH écrans, puis pause de SPLIT_HOLD_VH écrans
        s2.duration(splitUnits);
        tl.add(s2, T)
          .to({}, { duration: holdUnits }, T + splitUnits)
          // Le split reste fixe pendant que la section suivante le recouvre (voir coverNext)
          .to({}, { duration: coverUnits }, T + splitUnits + holdUnits);

        // Survol
        els.stage.addEventListener('pointermove', (e) => {
            const r = els.stage.getBoundingClientRect();
            pointerX = gsap.utils.clamp(0, 1, (e.clientX - r.left) / r.width);
            if (!interactive || isMobile()) return;
            // La moitié survolée prend le dessus : souris à droite → Design grandit
            toSplit(splitFromPointer(pointerX));
        });
        els.stage.addEventListener('pointerleave', () => {
            pointerX = null;
            if (interactive) toSplit(0.5);
        });
    }

    updateProgress(tl);

    initHomePractice(container);
    initHomeReveal(container);
    initHomeIndex(container);
    initHomeBackground(container);

    // ── Contact ──
    const lines = container.querySelectorAll('#home-contact-title > span > span');
    const cta   = container.querySelector('#home-contact-cta');

    if (lines.length) {
        gsap.fromTo(lines, { yPercent: 110 }, {
            yPercent: 0, stagger: 0.1, duration: 1.1, ease: 'expo.out',
            scrollTrigger: { trigger: '#home-contact-title', start: 'top 85%' },
        });
    }
    if (cta) {
        gsap.fromTo(cta, { opacity: 0, y: 20 }, {
            opacity: 1, y: 0, duration: 0.8, ease: 'power2.out',
            scrollTrigger: { trigger: cta, start: 'top 92%' },
        });
    }
};

// ─────────────────────────────────────────────
// Fond de la home en fondu
// Chaque section [data-home-bg] garde sa teinte en CSS (repli sans JS). Ici, on reporte
// ces teintes sur le conteneur de la page et on rend les sections transparentes :
// le fond change en fondu quand une section passe le milieu de l'écran, sans ligne de coupure.
// ─────────────────────────────────────────────

const initHomeBackground = (container) => {
    const sections = [...container.querySelectorAll('[data-home-bg]')];
    if (!sections.length) return;

    // Les sections restent OPAQUES (elles doivent masquer le split resté fixe dessous) :
    // leur fond suit une variable CSS animée, --home-bg, qui porte la teinte du fondu.
    // data-home-bg="solid" : la section garde sa propre teinte (ex. Pratique, qui recouvre le split).
    const colors = sections.map((sec) => getComputedStyle(sec).backgroundColor);
    container.style.setProperty('--home-bg', colors[0]);
    container.style.backgroundColor = 'var(--home-bg)';
    sections.forEach((sec) => {
        if (sec.dataset.homeBg !== 'solid') sec.style.backgroundColor = 'var(--home-bg)';
    });

    const fadeTo = (color) => gsap.to(container, {
        '--home-bg': color, duration: 0.9, ease: 'power2.out', overwrite: 'auto',
    });

    // Une frontière par section (sauf la première) : en descendant on prend sa teinte,
    // en remontant on revient à celle de la section précédente
    sections.forEach((sec, i) => {
        if (i === 0) return;
        ScrollTrigger.create({
            trigger: sec,
            start: 'top 55%',
            onEnter: () => fadeTo(colors[i]),
            onLeaveBack: () => fadeTo(colors[i - 1]),
        });
    });
};

// ─────────────────────────────────────────────
// Section 3 — Pratique : scroll normal, pas de pin
// La phrase s'éclaire mot à mot au fil du scroll, les paragraphes apparaissent ensuite.
// ─────────────────────────────────────────────

const initHomePractice = (container) => {
    const statement = container.querySelector('#home-practice-statement');

    if (statement) {
        // Découpe en mots (une seule fois, même si Barba ré-initialise la page)
        if (!statement.dataset.split) {
            const words = statement.textContent.trim().split(/\s+/);
            statement.textContent = '';
            words.forEach((w, i) => {
                const span = document.createElement('span');
                span.className = 'inline-block';
                span.textContent = w;
                statement.appendChild(span);
                if (i < words.length - 1) statement.appendChild(document.createTextNode(' '));
            });
            statement.dataset.split = 'true';
        }
        gsap.fromTo(statement.querySelectorAll('span'), { opacity: 0.12 }, {
            opacity: 1, ease: 'none', stagger: 0.1,
            scrollTrigger: { trigger: statement, start: 'top 80%', end: 'bottom 45%', scrub: 1 },
        });
    }
};

// ─────────────────────────────────────────────
// Section 3 — Textes (libellés et paragraphes de Pratique et Méthode) :
// même révélation partout. Chaque bloc .home-reveal découpe ses lignes masquées,
// qui montent l'une après l'autre quand le bloc entre dans l'écran (lecture, pas scrub).
// Les lignes sont calculées au moment de l'entrée : toujours justes, quelle que soit la largeur.
// ─────────────────────────────────────────────

const splitRevealWords = (el) => {
    if (el.dataset.split) return;
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const nodes  = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((node) => {
        // Espaces et retours à la ligne du HTML → une espace ; rien en début/fin de bloc.
        // L'espace insécable n'est pas une coupure : « privée : » reste d'un bloc.
        let text = node.nodeValue.replace(/[ \t\r\n]+/g, ' ');
        if (!node.previousSibling && node.parentNode === el) text = text.replace(/^ /, '');
        if (!node.nextSibling && node.parentNode === el) text = text.replace(/ $/, '');
        const frag = document.createDocumentFragment();
        text.split(/( )/).forEach((part) => {
            if (!part) return;
            if (part === ' ') { frag.appendChild(document.createTextNode(' ')); return; }
            const mask  = document.createElement('span');
            const inner = document.createElement('span');
            mask.className  = 'hm-w';
            inner.className = 'hm-i';
            inner.textContent = part;
            mask.appendChild(inner);
            frag.appendChild(mask);
        });
        node.parentNode.replaceChild(frag, node);
    });
    el.dataset.split = 'true';
};

const revealLines = (el) => {
    const lines = [];
    let lastTop = null;
    el.querySelectorAll('.hm-w').forEach((w) => {
        const top = Math.round(w.getBoundingClientRect().top);
        if (lastTop === null || Math.abs(top - lastTop) > 4) { lines.push([]); lastTop = top; }
        lines[lines.length - 1].push(w.firstElementChild);
    });
    return lines;
};

const initHomeReveal = (container) => {
    const blocks = [...container.querySelectorAll('.home-reveal')];
    const links  = [...container.querySelectorAll('.home-reveal-link')];
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    blocks.forEach((el) => {
        splitRevealWords(el);
        const inners = el.querySelectorAll('.hm-i');
        gsap.set(inners, { yPercent: 110 });

        ScrollTrigger.create({
            trigger: el,
            start: 'top 88%',
            onEnter: () => {
                revealLines(el).forEach((line, i) => {
                    gsap.to(line, { yPercent: 0, duration: 1, ease: 'power3.out', delay: i * 0.09, overwrite: true });
                });
            },
            // En remontant au-dessus du bloc, il se referme pour rejouer à la prochaine entrée
            onLeaveBack: () => gsap.to(inners, { yPercent: 110, duration: 0.4, ease: 'power2.in', overwrite: true }),
        });
    });

    links.forEach((link) => {
        gsap.set(link, { opacity: 0, y: 16 });
        ScrollTrigger.create({
            trigger: link,
            start: 'top 92%',
            onEnter:     () => gsap.to(link, { opacity: 1, y: 0, duration: 0.9, delay: 0.3, ease: 'power3.out', overwrite: true }),
            onLeaveBack: () => gsap.to(link, { opacity: 0, y: 16, duration: 0.3, overwrite: true }),
        });
    });
};

// ─────────────────────────────────────────────
// Section 4 — Sommaire des derniers projets : scroll normal
// À gauche la liste, à droite un cadre sticky qui montre le projet survolé.
// Au clic : même transition que le carrousel (l'image s'agrandit jusqu'à l'article).
// ─────────────────────────────────────────────

const initHomeIndex = (container) => {
    const list  = container.querySelector('#home-index-list');
    const frame = container.querySelector('#home-index-frame');
    if (!list) return;

    const rules = list.querySelectorAll('.home-index-rule');
    const links = [...list.querySelectorAll('.home-index-link')];
    const imgs  = frame ? [...frame.querySelectorAll('.home-index-img')] : [];
    const cap   = container.querySelector('#home-index-cap');
    const total = links.length;

    // Entrée : filets tracés, lignes en cascade, cadre en fondu
    gsap.fromTo(rules, { scaleX: 0 }, {
        scaleX: 1, duration: 1.1, stagger: 0.06, ease: 'expo.out',
        scrollTrigger: { trigger: list, start: 'top 85%' },
    });
    gsap.fromTo(links, { opacity: 0, y: 24 }, {
        opacity: 1, y: 0, duration: 0.8, stagger: 0.06, ease: 'power3.out',
        scrollTrigger: { trigger: list, start: 'top 85%' },
        onComplete: () => { gsap.set(links, { clearProps: 'opacity' }); setActive(wanted); },
    });
    if (frame) {
        gsap.fromTo(frame, { opacity: 0, y: 40 }, {
            opacity: 1, y: 0, duration: 1, ease: 'power3.out',
            scrollTrigger: { trigger: list, start: 'top 80%' },
        });
    }

    // Images du cadre : chargées en différé dans le HTML (pour ne pas alourdir l'arrivée sur la home),
    // puis toutes lancées dès que la section approche
    if (imgs.length) {
        ScrollTrigger.create({
            trigger: container.querySelector('#home-index'),
            start: 'top bottom+=50%',
            once: true,
            onEnter: () => imgs.forEach((im) => { if (im.tagName === 'IMG') im.loading = 'eager'; }),
        });
    }

    // wanted : projet survolé (ligne pleine, légende) · shown : image réellement affichée dans le cadre
    let wanted = 0;
    let shown  = 0;
    gsap.set(imgs, { autoAlpha: 0 });
    if (imgs[0]) gsap.set(imgs[0], { autoAlpha: 1, zIndex: 1 });

    // ── Transition « Column Random Grid » (Codrops, SVG Mask Transitions — même mécanique que hero-carousel.js) ──
    // L'image actuelle disparaît cellule par cellule (colonnes de gauche à droite, ordre aléatoire
    // dans chaque colonne) et révèle l'image entrante, avec un flash blanc sur ~18 % des cellules.
    const mask = frame ? buildIndexMask(frame) : null;
    let maskTl = null;
    let maskTarget = -1;

    // Termine immédiatement une transition en cours (nouveau survol ou clic)
    const commitMask = () => {
        if (!maskTl) return;
        maskTl.progress(1);
    };

    const setActive = (i) => {
        wanted = i;
        links.forEach((l, k) => l.classList.toggle('opacity-40', k !== i));
        if (cap) cap.textContent = String(i + 1).padStart(2, '0') + ' / ' + String(total).padStart(2, '0');

        const next = imgs[i];
        if (!next || i === shown || i === maskTarget) return;

        // Image pas encore chargée : on la force, et on l'affiche dès qu'elle est décodée si elle est toujours demandée
        if (next.tagName === 'IMG' && !next.complete) {
            next.loading = 'eager';
            next.decode().catch(() => {}).then(() => { if (wanted === i) setActive(i); });
            return;
        }

        commitMask();

        const reveal = () => {
            const prev = imgs[shown];
            gsap.set(next, { autoAlpha: 1, zIndex: 2 });
            if (prev && prev !== next) gsap.set(prev, { autoAlpha: 0, zIndex: 0 });
            imgs.forEach((im, k) => { if (k !== i) gsap.set(im, { autoAlpha: 0, zIndex: 0 }); });
            shown = i;
        };

        // Pas de masque possible (vignette sans image) : simple bascule
        if (!mask || next.tagName !== 'IMG') { reveal(); return; }

        const front = imgs[shown];
        const frontSrc = front && front.tagName === 'IMG' ? (front.currentSrc || front.src) : '';
        maskTarget = i;
        maskTl = mask.play(frontSrc, next.currentSrc || next.src, () => { reveal(); maskTl = null; maskTarget = -1; });
    };

    const hasHover = window.matchMedia('(hover: hover)').matches;

    links.forEach((link, i) => {
        if (hasHover) link.addEventListener('pointerenter', () => setActive(i));

        link.addEventListener('click', (e) => {
            e.preventDefault();
            // Image de départ : le cadre (desktop) ou la vignette de la ligne (mobile / pas de cadre visible)
            const frameVisible = frame && frame.offsetParent !== null;
            let img = null;
            if (frameVisible && imgs[i]) {
                commitMask();
                gsap.killTweensOf(imgs[i]);
                gsap.set(imgs[i], { autoAlpha: 1, clipPath: 'inset(0% 0% 0% 0%)', scale: 1, zIndex: 3 });
                img = imgs[i].tagName === 'IMG' ? imgs[i] : null;
            } else {
                img = link.querySelector('.home-index-thumb img');
            }
            if (img) {
                startArticleTransition(link, img);
            } else {
                barba.go(link.dataset.url);
            }
        });
    });

    // Quand on quitte la liste, le cadre garde le dernier projet survolé (pas de retour à vide)
    list.addEventListener('pointerleave', () => links.forEach((l, k) => l.classList.toggle('opacity-40', k !== wanted)));
};

// ─────────────────────────────────────────────
// Masque « Column Random Grid » du cadre du sommaire
// Même technique que hero-carousel.js (Codrops, SVG Mask Transitions on Scroll) :
//   · calque arrière = image entrante, calque avant = image actuelle, masquée
//   · masque = fond blanc + cellules noires dont l'opacité passe de 0 à 1
//     → l'image actuelle disparaît cellule par cellule
//   · ordre : colonne par colonne de gauche à droite, mélangé dans chaque colonne
//   · flash blanc léger sur ~18 % des cellules, au moment où chacune s'ouvre
// Ici la timeline est JOUÉE au survol (et non pilotée par la molette / le scroll).
// ─────────────────────────────────────────────

const INDEX_MASK = {
    cols: 7,            // colonnes ; les rangées sont calculées pour des cellules ~carrées
    cellDur: 0.6,       // durée d'ouverture d'une cellule
    each: 0.01,         // décalage entre deux cellules (stagger.each)
    ease: 'power3.out',
    flashRatio: 0.18,   // part des cellules qui flashent (0 = aucun flash)
    flashOpacity: 0.25, // intensité du flash (1 = blanc plein)
    flashDur: 0.12,
};
let indexMaskUid = 0;

const buildIndexMask = (frame) => {
    const box = frame.querySelector('.home-index-img')?.parentElement;
    if (!box) return null;

    const NS = 'http://www.w3.org/2000/svg';
    const id = 'home-index-mask-' + (++indexMaskUid);
    const el = (tag, attrs = {}) => {
        const n = document.createElementNS(NS, tag);
        Object.entries(attrs).forEach(([k, v]) => n.setAttribute(k, v));
        return n;
    };

    const svg      = el('svg', { preserveAspectRatio: 'none', 'aria-hidden': 'true' });
    svg.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;z-index:3;pointer-events:none;visibility:hidden';
    const defs     = el('defs');
    const maskEl   = el('mask', { id, maskUnits: 'userSpaceOnUse', x: 0, y: 0 });
    const maskBase = el('rect', { x: 0, y: 0, height: 100, fill: 'white' });  // blanc = image avant visible
    const gridG    = el('g');                                                 // cellules noires = zones cachées
    maskEl.append(maskBase, gridG);
    defs.appendChild(maskEl);

    const imgAttrs   = { x: 0, y: 0, height: 100, preserveAspectRatio: 'xMidYMid slice' }; // = object-fit: cover
    const imageBack  = el('image', imgAttrs);                                  // image entrante
    const imageFront = el('image', { ...imgAttrs, mask: `url(#${id})` });       // image actuelle, masquée
    const flashG     = el('g');                                                 // flashs blancs
    svg.append(defs, imageBack, imageFront, flashG);
    box.appendChild(svg);

    let cells = [];
    let vbW = 0;
    const vbH = 100;

    // Grille recalculée si le ratio du cadre change (responsive)
    const buildGrid = () => {
        const w = box.clientWidth, h = box.clientHeight;
        if (!w || !h) return false;
        const nextVbW = (w / h) * vbH;
        if (cells.length && Math.abs(nextVbW - vbW) < 0.01) return true;
        vbW = nextVbW;

        svg.setAttribute('viewBox', `0 0 ${vbW} ${vbH}`);
        [imageBack, imageFront, maskBase].forEach((n) => n.setAttribute('width', vbW));
        maskEl.setAttribute('width', vbW);
        maskEl.setAttribute('height', vbH);

        gridG.innerHTML = '';
        flashG.innerHTML = '';
        cells = [];
        const cols  = INDEX_MASK.cols;
        const rows  = Math.max(1, Math.round(cols * (vbH / vbW)));
        const cellW = vbW / cols;
        const cellH = vbH / rows;

        for (let y = 0; y < rows; y++) {
            for (let x = 0; x < cols; x++) {
                const attrs = {
                    x: x * cellW, y: y * cellH,
                    width: cellW + 0.1, height: cellH + 0.1, // +0.1 : anti-jours sous-pixels
                    'shape-rendering': 'crispEdges',
                };
                const rect  = el('rect', { ...attrs, fill: 'black' });
                const flash = el('rect', { ...attrs, fill: 'white' });
                rect.style.opacity = '0';
                flash.style.opacity = '0';
                gridG.appendChild(rect);
                flashG.appendChild(flash);
                cells.push({ el: rect, flashEl: flash, col: x });
            }
        }
        return true;
    };

    // Colonne par colonne de gauche à droite, ordre mélangé dans chaque colonne
    const buildColumnOrder = () => {
        const byColumn = Array.from({ length: INDEX_MASK.cols }, () => []);
        cells.forEach((c) => byColumn[c.col].push(c));
        return byColumn.flatMap((column) => gsap.utils.shuffle(column));
    };

    return {
        play(frontSrc, backSrc, onDone) {
            if (!buildGrid() || !frontSrc) { onDone(); return null; }

            imageFront.setAttribute('href', frontSrc);
            imageBack.setAttribute('href', backSrc);

            const order = buildColumnOrder();
            gsap.set(cells.map((c) => c.el), { opacity: 0 });
            gsap.set(cells.map((c) => c.flashEl), { opacity: 0 });
            gsap.set(svg, { visibility: 'visible' });

            const tl = gsap.timeline({
                onComplete: () => {
                    onDone();
                    gsap.set(svg, { visibility: 'hidden' });
                },
            });

            tl.to(order.map((c) => c.el), {
                opacity: 1,
                duration: INDEX_MASK.cellDur,
                ease: INDEX_MASK.ease,
                stagger: { each: INDEX_MASK.each },
            }, 0);

            // Flash bref (yoyo) sur une partie des cellules, synchronisé avec leur ouverture
            order.forEach((c, i) => {
                if (Math.random() > INDEX_MASK.flashRatio) return;
                tl.to(c.flashEl, {
                    opacity: INDEX_MASK.flashOpacity, duration: INDEX_MASK.flashDur, ease: 'power1.out', yoyo: true, repeat: 1,
                }, i * INDEX_MASK.each);
            });

            return tl;
        },
    };
};

// Retour depuis un article : remet la scène exactement où on l'avait laissée (sans rattrapage du scrub)
export const restoreHomeFlow = (scrollY, lenis) => {
    window.scrollTo(0, scrollY);
    lenis.scrollTo(scrollY, { immediate: true, force: true });
    ScrollTrigger.update();
    const st = ScrollTrigger.getById('home-flow');
    if (st?.animation) st.animation.progress(st.progress);
};